import get from 'lodash/get';
import styled from 'styled-components';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EyeSolid,
  ProgressCircleHalf,
  RedoAlt,
} from '@alefeducation/ui-elements-library-icons';
import { StudentAttachmentFile } from '../StudentAttachmentFile';
import getLocaleDate from '../../../utils/getLocaleDate';
import { isLateSubmission } from '../../../utils/isLateSubmission';
import icons from '../icons';
import { GradeScore } from '../GradeScore';
import GradeFeedback from '../GradeFeedback';
import {
  isAssignmentGraded,
  isAssignmentReturned,
  isAssignmentSubmitted,
} from '../../../utils/assignmentStatusUtils';
import assignmentTypes from '../../../utils/assignmentTypes';
import UnlockedIcon from '../../common/Icons/UnlockedIcon';
import AssignmentContext from '../../common/AssignmentContext/AssignmentContext';
import {
  getUnassignTooltip,
  isNcleAlefAssignment,
} from '../../../utils/isNcleAlefAssignment';
import StarsAward from '../../common/StarsAward/StarsAward';
import getStudentTeamIcons from '../getStudentTeamIcons';
import { useStudentSubmissionDispatch } from '../../AssignmentSubmissionEvaluate/StudentSubmissionPageContext';
import { DateColumn, TeamIcon } from './StudentListSection.styled';
import { useStudentListContext } from './StudentListContext';
import { StudentInfo } from './StudentListSection';
import AssignedContentStatus from './AssignedContentStatus';
import {
  useUpdateAssignStatusToAssignment,
  LockUnlockIconSpinner,
} from './useUpdateAssignStatusToAssignment';
import { renderUnassignIcon } from './UnassignAllIcon';

export const useCommonSectionItemElements = (student, submission) => {
  const { t, i18n } = useTranslation();
  const IPAD_DESIGN_BREAK_POINT = 1024;
  const {
    maxScore,
    maxStars,
    attachmentRequired,
    commentRequired,
    onFeedbackClick,
    onAddFeedbackClick,
    onGradeScoreFocus,
    onGradeScoreBlur,
    focusedGradeInputStudentId,
    gradeable,
    dueOn,
    teamsData,
    isLockingOfAssignmentEnabled,
  } = useStudentListContext();

  const {
    assignmentParams: { isNcle },
    assignmentData: { assignmentType },
  } = useContext(AssignmentContext);
  const isNCLE = isNcleAlefAssignment(isNcle, assignmentType);

  const {
    onUpdateAssignStatusStudents,
    updatingStatusInProgress,
  } = useUpdateAssignStatusToAssignment();
  const dispatch = useStudentSubmissionDispatch();

  const response = submission ? submission.response : null;
  const isSubmitted = !!(
    submission && isAssignmentSubmitted(submission.status)
  );
  const isReturned = !!(submission && isAssignmentReturned(submission.status));
  const isGraded = !!(submission && isAssignmentGraded(submission.status));
  const isResponseGiven = !!get(submission, 'response.comment');
  const assignmentTypeForPendo = assignmentType
    .split('_')
    .join('-')
    .toLowerCase();

  const handleGradeScoreInputFocus = studentToGrade => () => {
    onGradeScoreFocus(studentToGrade.id);
  };
  const handleGradeScoreInputBlur = studentToGrade => () => {
    onGradeScoreBlur(studentToGrade.id);
  };
  return {
    unassignHandle: isLockingOfAssignmentEnabled && (
      <UnassignButtonWrapper
        onClick={onUpdateAssignStatusStudents(
          [student],
          AssignedContentStatus.UnAssigned,
          true,
        )}
        data-testid={`unassign-student-${student.id}`}
        data-tip={t('Lock')}
        className={`data-analytics-${assignmentTypeForPendo}-class-dashboard-single-lock`}
      >
        {updatingStatusInProgress ? (
          <LockUnlockIconSpinner small />
        ) : (
          <UnlockedIcon size={28} />
        )}
      </UnassignButtonWrapper>
    ),
    studentInfo: (
      <StudentInfo
        avatar={student.avatar}
        studentName={student.name}
        studentUsername={student.username}
      />
    ),
    dateColumns: (
      <>
        <DateColumn
          className="submitted-on-date-column"
          submission={submission}
        >
          {submission &&
            submission.submittedOn && (
              <div>
                <div className="date-title">Submitted</div>
                <div>
                  <span data-tip={t('Submitted Date')}>
                    {getLocaleDate({
                      date: submission.submittedOn,
                      language: i18n.language,
                    })}
                  </span>
                  {assignmentType === assignmentTypes.TEACHER &&
                    isLateSubmission(dueOn, submission.submittedOn) && (
                      <icons.Time data-testid="late-time-icon" />
                    )}
                </div>
              </div>
            )}
        </DateColumn>
        <DateColumn
          className="evaluated-on-date-column"
          submission={submission}
        >
          {submission &&
            submission.status !== 'PENDING' &&
            submission.evaluatedOn && (
              <div>
                <div className="date-title">Evaluated</div>
                <span data-tip={t('Evaluated Date')}>
                  {getLocaleDate({
                    date: submission.evaluatedOn,
                    language: i18n.language,
                  })}
                </span>
              </div>
            )}
        </DateColumn>
        <DateColumn className="returned-on-date-column" submission={submission}>
          {submission &&
            submission.returnedOn && (
              <div>
                <div className="date-title">Returned</div>
                <span data-tip={t('Returned Date')}>
                  {getLocaleDate({
                    date: submission.returnedOn,
                    language: i18n.language,
                  })}
                </span>
              </div>
            )}
        </DateColumn>
      </>
    ),
    gradeField: gradeable && (
      <GradeScore
        submission={submission}
        isSubmitted={!!isSubmitted}
        isReturned={!!isReturned}
        response={response}
        maxScore={maxScore}
        student={student}
        isGraded={isGraded}
        attachmentRequired={attachmentRequired}
        commentRequired={commentRequired}
        onFocus={handleGradeScoreInputFocus(student)}
        onBlur={handleGradeScoreInputBlur(student)}
        isActiveStudentInTheClass={student.active}
      />
    ),
    feedbackField: (
      <GradeFeedback
        disabled={focusedGradeInputStudentId === student.id}
        onAddFeedbackClick={() => {
          onAddFeedbackClick(submission, student);
        }}
        isReturned={!!isReturned}
        isSubmitted={!!isSubmitted}
        onFeedbackClick={() => {
          onFeedbackClick(submission, student);
        }}
        isResponseGiven={isResponseGiven}
        attachmentRequired={attachmentRequired}
        gradeable={gradeable}
        commentRequired={commentRequired}
        pendoClassName={`data-analytics-${assignmentTypeForPendo}-class-dashboard-feedback`}
      />
    ),
    attachmentField: ({ withLabel } = {}) =>
      submission &&
      submission.attachment && (
        <StudentAttachmentWrapper
          onClick={e => e.stopPropagation()}
          className={`data-analytics-${assignmentTypeForPendo}-class-dashboard-download-file`}
        >
          <StudentAttachmentFile
            withLabel={withLabel}
            student={student}
            submission={submission}
          />
        </StudentAttachmentWrapper>
      ),
    starsAward: get(submission, 'response.stars', null) !== null &&
      assignmentType === assignmentTypes.ALEF && (
        <StarsAwardStyled
          max={maxStars}
          earned={get(submission, 'response.stars', 0)}
        />
      ),
    teamIcons: (
      <TeamIconWrapper>
        {getStudentTeamIcons(teamsData, student.id).map(({ icon, name }) => (
          <TeamIcon
            data-tip={name}
            data-testid={`team-icon--${icon}`}
            key={icon}
            className={`assignments-icon ${icon} colored`}
          />
        ))}
      </TeamIconWrapper>
    ),
    requestResubmitField: (
      <RequestResubmissionIconWrapper
        onClick={e => {
          e.stopPropagation();
          dispatch({
            type: 'openRequestResubmissionModal',
            payload: {
              studentId: student.id,
            },
          });
        }}
        data-tip={t('Request Resubmission')}
        className={`data-analytics-${assignmentTypeForPendo}-class-dashboard-request-resubmit`}
      >
        <RedoAlt fontSize={16} />
      </RequestResubmissionIconWrapper>
    ),
    viewIcon: (row, onViewStudent) => (
      <ViewIconWrapper
        onClick={() => {
          onViewStudent(row);
        }}
        data-tip={t('View')}
        className={`data-analytics-${assignmentTypeForPendo}-class-dashboard-view-student-submission`}
      >
        <EyeSolid fontSize={18} />
      </ViewIconWrapper>
    ),
    newUnAssignHandle: isLockingOfAssignmentEnabled && (
      <NewUnAssignButtonWrapper
        onClick={onUpdateAssignStatusStudents(
          [student],
          AssignedContentStatus.UnAssigned,
          true,
        )}
        data-testid={`unassign-student-${student.id}`}
        data-tip={t(getUnassignTooltip(isNCLE))}
      >
        {updatingStatusInProgress ? (
          <ProgressCircleHalf size={18} />
        ) : (
          renderUnassignIcon(isNCLE)
        )}
      </NewUnAssignButtonWrapper>
    ),
    IPAD_DESIGN_BREAK_POINT,
  };
};

const UnassignButtonWrapper = styled.div`
  cursor: pointer;
`;

const StudentAttachmentWrapper = styled.div`
  display: flex;
`;

const StarsAwardStyled = styled(StarsAward)`
  & svg {
    width: 14px;
    height: 14px;
  }
`;

const TeamIconWrapper = styled.div`
  display: flex;
  flex: 2;
  align-items: center;
`;

export const ContextMenuList = styled.ul`
  padding: 0 12px;

  & > li {
    display: flex;
    margin-bottom: 10px;

    &:last-of-type {
      margin-bottom: 0;
    }

    & > div {
      display: inline-flex;
    }

    & span {
      margin-left: 10px;
      white-space: nowrap;
    }
  }
`;

const RequestResubmissionIconWrapper = styled.div`
  cursor: pointer;
  height: 32px;
  width: 32px;
`;

const ViewIconWrapper = styled.div`
  cursor: pointer;
  height: 44px;
  width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NewUnAssignButtonWrapper = styled.div`
  border-radius: 0;
  width: 48px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: flex-end;

  &:hover {
    cursor: pointer;
  }
`;
